import { type UnknownAction } from "@reduxjs/toolkit";

import {
  FETCH_ANSWER_FOR_SEEK_REQUEST,
  FETCHING_ANSWER_FOR_SEEK_SUCCEEDED,
  FETCH_SEEK_SESSIONS_REQUEST,
  FETCHING_SEEK_SESSIONS_SUCCEEDED,
  FETCHING_SEEK_SESSIONS_FAILED,
  DELETE_SEEK_SESSION_REQUEST,
  DELETING_SEEK_SESSION_SUCCEEDED,
  DELETE_SEEK_SESSIONS_REQUEST,
  DELETING_SEEK_SESSIONS_SUCCEEDED,
  DELETING_SEEK_SESSIONS_FAILED,
  FETCH_SEEK_SESSION_REQUEST,
  FETCHING_SEEK_SESSION_SUCCEEDED,
  FETCHING_SEEK_SESSION_FAILED,
  FETCH_SEEK_EMAIL_RESPONSE,
  FETCHING_SEEK_EMAIL_RESPONSE_SUCCEEDED,
  FETCHING_SEEK_EMAIL_RESPONSE_FAILED,
  INVALIDATE_SEEK_EMAIL_RESPONSE,
  UPDATE_SEEK_FEEDBACK,
  UPDATING_SEEK_FEEDBACK_SUCCEEDED,
  FETCHING_SEEK_LIMIT_SUCCEEDED,
  FETCHING_SEEK_LIMIT_FAILED,
  FETCH_SEEK_PROMPTS,
  FETCHING_SEEK_PROMPTS_SUCCEEDED,
  FETCHING_SEEK_PROMPTS_FAILED,
  INVALIDATE_SEEK_SESSION,
  TOGGLE_SEEK,
  FETCHING_ANSWER_FOR_SEEK_FAILED,
} from "src/redux/actions/actionTypes";

import {
  SeekAnswerRequest,
  SeekError,
  SeekAnswerResponse,
  SeekState,
  SeekHistorySessions,
  SeekEmailResponse,
} from "src/types/seek";

const initialSeekState: SeekState = {
  loading: false,
  success: false,
  error: {
    message: "",
    code: 0,
  },
  prompts: {
    loading: false,
    success: false,
    items: [],
    error: {
      message: "",
      code: 0,
    },
  },
  session: {
    loading: false,
    item: {
      id: "",
      name: "",
      createdDate: 0,
      chats: [],
    },
    error: {
      message: "",
      code: 0,
    },
  },
  sessions: {
    items: [],
    loading: false,
    loadMore: false,
    deleting: false,
    deleted: false,
    pagination: {
      currentPage: 0,
      totalPages: 0,
      totalRecordsCount: 0,
      recordsPerPage: 0,
      records: [],
    },
  },
  email: {
    session: "",
    id: "",
    loading: false,
    success: false,
    body: "",
  },
  userLimit: 0,
  accountLimit: 0,
};

export function seekReducer(state = initialSeekState, action: UnknownAction) {
  switch (action.type) {
    case FETCH_ANSWER_FOR_SEEK_REQUEST: {
      const { question, id } = action.payload as SeekAnswerRequest;

      // If id is null, replace chats with only the new chat (start fresh)
      // Otherwise append the new chat to existing chats
      const chats = id === null
        ? [{
            id,
            question,
            answer: "",
            citations: [],
            loading: true,
          }]
        : [
            ...state.session.item.chats,
            {
              id,
              question,
              answer: "",
              citations: [],
              loading: true,
            }
          ];
    
      return {
        ...state,
        loading: false,
        session: {
          ...state.session,
          loading: true,
          item: {
            ...state.session.item,
            chats,
          }
        }
      };
    }

    case FETCHING_ANSWER_FOR_SEEK_SUCCEEDED: {
      const { id, response } = action.payload as {
        response: SeekAnswerResponse;
        id: string;
      };

      return {
        ...state,
        loading: false,
        session: {
          ...state.session,
          loading: false,
          item: {
            ...state.session.item,
            id: response.session,
            chats: state.session.item.chats.map((chat) =>
              chat.id === id
                ? {
                    ...chat,
                    id: response.id,
                    session: response.session,
                    action: response.action,
                    answer: response.answer,
                    citations: response.citations,
                    contents: response.contents,
                    loading: false,
                  }
                : chat,
            ),
          },
          error: {
            message: "",
            code: 0,
          },
        },
      };
    }

    case FETCHING_ANSWER_FOR_SEEK_FAILED: {
      const response = action.payload as any;

      return {
        ...state,
        loading: false,
        session: {
          ...state.session,
          loading: false,
          item: {
            chats: []
          },
          error: {
            message: response.errors[0].message,
            code: response.errors[0].code,
          },
        },
      };
    }

    case FETCH_SEEK_SESSIONS_REQUEST: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          loading: true,
        },
      };
    }

    case FETCHING_SEEK_SESSIONS_SUCCEEDED: {
      const response = action.payload as SeekHistorySessions;

      const updatedItems =
        response.currentPage > 0
          ? [...state.sessions.items, ...response.records]
          : response.records;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          loading: false,
          items: updatedItems,
          currentPage: response.currentPage,
          totalPages: response.totalPages,
        },
      };
    }

    case FETCHING_SEEK_SESSIONS_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          loading: false,
          error: {
            message: error.message,
            code: error.code,
          },
        },
      };
    }

    case DELETE_SEEK_SESSION_REQUEST: {
      return {
        ...state,
        session: {
          ...state.session,
          deleting: true,
        },
      };
    }

    case DELETING_SEEK_SESSION_SUCCEEDED: {
      const { id } = action.payload as { id: string };
      const items = [...state.sessions.items].filter(
        (item: any) => item.id !== id,
      );

      return {
        ...state,
        sessions: {
          ...state.sessions,
          items: items,
        },
      };
    }

    case DELETE_SEEK_SESSIONS_REQUEST: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          deleting: true,
          deleted: false
        },
      };
    }

    case DELETING_SEEK_SESSIONS_SUCCEEDED: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          items: [],
          deleting: false,
          deleted: true
        },
      };
    }

    case DELETING_SEEK_SESSIONS_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          deleting: false,
          deleted: false,
          error: {
            message: error.message,
            code: error.code,
          },
        },
      };
    }

    case FETCH_SEEK_SESSION_REQUEST: {
      const { id } = action.payload as { id: string };
      return {
        ...state,
        session: {
          loading: true,
          item: {
            id: id,
            chats: [],
          },
        },
      };
    }

    case FETCHING_SEEK_SESSION_SUCCEEDED: {
      const response = action.payload as SeekHistorySessions;

      return {
        ...state,
        session: {
          ...state.session,
          loading: false,
          history: true,
          item: {
            ...state.session.item,
            name: "",
            createdDate: 0,
            chats: response.records,
          },
        },
      };
    }

    case FETCHING_SEEK_SESSION_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        session: {
          ...state.session,
          loading: false,
          error: {
            message: error.message,
            code: error.code,
          },
        },
      };
    }

    case FETCH_SEEK_EMAIL_RESPONSE: {
      return {
        ...state,
        email: {
          ...state.email,
          loading: true,
          success: false,
        },
      };
    }

    case FETCHING_SEEK_EMAIL_RESPONSE_SUCCEEDED: {
      const response = action.payload as SeekEmailResponse;

      return {
        ...state,
        email: {
          ...state.email,
          loading: false,
          body: response.body,
          success: true,
        },
      };
    }

    case FETCHING_SEEK_EMAIL_RESPONSE_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        email: {
          ...state.email,
          loading: false,
          success: false,
          error: {
            message: error.message,
            code: error.code,
          },
        },
      };
    }

    case INVALIDATE_SEEK_EMAIL_RESPONSE: {
      return {
        ...state,
        email: {
          ...state.email,
          loading: false,
          success: false,
          body: "",
        },
      };
    }

    case UPDATE_SEEK_FEEDBACK: {
      const { id } = action.payload as { id: string };

      return {
        ...state,
        session: {
          ...state.session,
          item: {
            ...state.session.item,
            chats: state.session.item.chats.map((chat) =>
              chat.id === id
                ? {
                    ...chat,
                    updating: true,
                  }
                : chat,
            ),
          },
        },
      };
    }

    case UPDATING_SEEK_FEEDBACK_SUCCEEDED: {
      const response = action.payload as any;

      return {
        ...state,
        session: {
          ...state.session,
          item: {
            ...state.session.item,
            chats: state.session.item.chats.map((chat) =>
              chat.id === response.id
                ? {
                    ...chat,
                    helpful: response.helpful,
                    updating: false,
                  }
                : chat,
            ),
          },
        },
      };
    }

    case FETCHING_SEEK_LIMIT_SUCCEEDED: {
      const response = action.payload as { userLimit: number; accountLimit: number };

      return {
        ...state,
        loading: false,
        userLimit: response.userLimit,
        accountLimit: response.accountLimit,
      };
    }

    case FETCHING_SEEK_LIMIT_FAILED: {
      const error = action.payload as SeekError;

      return {
        ...state,
        loading: false,
        error: {
          message: error.message,
          code: error.code,
        },
      };
    }

    case FETCH_SEEK_PROMPTS: {
      return {
        ...state,
        prompts: {
          ...state.prompts,
          loading: true,
        },
      };
    }

    case FETCHING_SEEK_PROMPTS_SUCCEEDED: {
      const {suggestions} = action.payload as {suggestions: Array<string>};

      return {
        ...state,
        prompts: {
          ...state.prompts,
          loading: false,
          items: suggestions,
        },
      };
    }

    case FETCHING_SEEK_PROMPTS_FAILED: {  
      const error = action.payload as SeekError;

      return {
        ...state,
        prompts: {
          ...state.prompts,
          loading: false,
          error: {
            message: error.message,
            code: error.code,
          },
        },
      };
    }

    case INVALIDATE_SEEK_SESSION: {
      return {
        ...state,
        session: {
          ...state.session,
          loading: false,
          item: {
            id: '',
            name: "",
            createdDate: "",
            chats: [],
          },
        },
      };
    }

    case TOGGLE_SEEK: {
      return {
        ...state,
        success: !state.success,
        session: {
          ...state.session,
          item: {
            ...state.session.item,
            id: '',
          },
        },
      };
    }

    default: {
      return state;
    }
  }
}
